import cn from 'classnames';
import React, { InputHTMLAttributes } from 'react';
import TrashIcon from '@components/icons/trash';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  inputClassName?: string;
  label?: string;
  name: string;
  error?: string;
  type?: string;
  shadow?: boolean;
  required?: boolean;
  variant?: 'normal' | 'solid' | 'outline' | 'line';
  dimension?: 'small' | 'medium' | 'big';
  dynamic?: boolean;
  itemPosition?: number;
  onRemove?: (index: number) => void;
  labelClass?: string;
}

const variantClasses = {
  normal:
    'bg-gray-100 border border-border-base rounded focus:shadow focus:bg-light focus:border-accent',
  solid:
    'bg-gray-100 border border-border-100 rounded focus:bg-light focus:border-accent',
  outline: 'border border-border-base rounded focus:border-accent',
  line: 'ps-0 border-b border-border-base rounded-none focus:border-accent',
};

const sizeClasses = {
  small: 'text-sm h-10',
  medium: 'h-13.5',
  big: 'h-13.5',
};

const Input = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      label,
      name,
      error,
      children,
      variant = 'normal',
      dimension = 'medium',
      shadow = false,
      disabled = false,
      type = 'text',
      inputClassName,
      itemPosition,
      dynamic = false,
      required = false,
      labelClass = '',
      onRemove = () => {},
      ...rest
    },
    ref
  ) => {
    return (
      <div className={className}>
        {label && (
          <label
            htmlFor={name}
            className={cn("block text-body-dark text-sm leading-none mb-2", dynamic && "items-center flex justify-between", labelClass)}
          >
            <span>
              {label}
              {required && (<span className="text-red-500">*</span>)}
            </span>
            {dynamic && <span className="cursor-pointer" onClick={() => onRemove(itemPosition as number)}>
              <TrashIcon />
            </span>}
          </label>
        )}
        <input
          id={name}
          name={name}
          type={type}
          ref={ref}
          className={cn(
            'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out font-semibold text-heading text-sm focus:outline-none focus:ring-0',
            shadow && 'focus:shadow',
            variantClasses[variant],
            sizeClasses[dimension],
            disabled && 'bg-gray-100 cursor-not-allowed',
            inputClassName
          )}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          aria-invalid={error ? 'true' : 'false'}
          // @ts-ignore
          onWheel={(e) => e.target.blur()}
          {...rest}
        />
        {error && <p className="my-2 text-xs text-red-500">{error}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';
export default Input;
